import { useNotification } from "@expert/common-ui";
import type { ProductInfo } from "@expert/contentful";
import { Box, Button, Checkbox, CloseButton, Flex, Group, Input, Modal, Select, Text, Title } from "@mantine/core";
import { type JSX, useEffect, useState } from "react";
import { IMaskInput } from "react-imask";
import { useAnalytics } from "@/analytics";
import { useVerifiedCustomerStore } from "@/customer-info";
import { usePartnerConfig } from "@/partner/usePartnerConfig";
import type { EligibleProduct } from "@/sales-tools";
import { createEnrollment, postDeclination, useProductOfferStore } from "@/sales-tools";
import { getSelectedSessionId, usePartner, useSessionId, useTypedMostRecentTask } from "@/sdk";
import { EnrollmentStatus, SaleProgress } from "../../home-product/types";
import classes from "./SaleTrackingModal.module.css";

interface SaleTrackingModalProps {
    opened: boolean;
    onClose: () => void;
    productInfo: ProductInfo | undefined;
    selectedProduct: EligibleProduct;
    skuProductMap?: Record<string, string>;
}

export function SaleTrackingModal({
    opened,
    onClose,
    productInfo,
    selectedProduct,
    skuProductMap,
}: SaleTrackingModalProps): JSX.Element | null {
    const { dispatcher } = useAnalytics();
    const [isLoadingEnroll, setIsLoadingEnroll] = useState(false);
    const [isLoadingDecline, setIsLoadingDecline] = useState(false);
    const [codeValue, setCodeValue] = useState("");
    const [isConfirmationChecked, setIsConfirmationChecked] = useState(false);
    const [additionalSku, setAdditionalSku] = useState("");
    const { isVoice } = useTypedMostRecentTask();
    const partner = usePartner();
    const sessionId = useSessionId();
    const { customer } = useVerifiedCustomerStore((state) => state.getVerifiedCustomer(sessionId));
    const { blocklistedConfirmationCodes } = usePartnerConfig();

    if (!isVoice) {
        throw new Error("Active task is not voice task");
    }
    useEffect(() => {
        void dispatcher.dispatchAction("DialogShown", "SaleTrackingModal", { productSku: selectedProduct.productSku });
    }, [dispatcher, selectedProduct.productSku]);
    const notification = useNotification();

    const closeAndClear = () => {
        setCodeValue("");
        setAdditionalSku("");
        setIsConfirmationChecked(false);
        useProductOfferStore.getState().updateSelectedProduct(sessionId, undefined);
        onClose();
    };
    const onAccept = async () => {
        setIsLoadingEnroll(true);

        const additionalPlanProductOfferId = additionalSku !== "NO" ? skuProductMap?.[additionalSku] : undefined;

        void dispatcher.dispatchAction("ButtonClick", "Enroll", {
            productSku: [selectedProduct.productSku, additionalSku !== "NO" ? additionalSku : undefined].filter(
                Boolean,
            ),
            confirmationCode: codeValue,
        });

        const productOffers = [{ productOfferId: selectedProduct.id, productSku: selectedProduct.productSku }];
        if (additionalPlanProductOfferId) {
            productOffers.push({ productOfferId: additionalPlanProductOfferId, productSku: additionalSku });
        }
        const shouldIncludeAccountNumber = customer?.accountNumber && selectedProduct.productSku === "VZN_VHDP";
        const responses = await Promise.allSettled(
            productOffers.map(({ productOfferId, productSku }) => {
                return createEnrollment({
                    sessionId,
                    partner,
                    productOfferId,
                    confirmationCode: codeValue,
                    productSku,
                    ...(shouldIncludeAccountNumber && {
                        additionalData: {
                            accountNumber: customer.accountNumber,
                        },
                    }),
                });
            }),
        );

        responses.forEach((response) => {
            if (response.status === "fulfilled" && response.value.enrollmentStatus === EnrollmentStatus.Success) {
                useProductOfferStore.getState().updateProduct(sessionId, selectedProduct.id, {
                    ...selectedProduct,
                    saleProgress: SaleProgress.Success,
                });
            } else {
                notification.error("There was an error processing the enrollment");
            }
        });
        setIsLoadingEnroll(false);
        closeAndClear();
    };

    const onDecline = async () => {
        setIsLoadingDecline(true);
        void dispatcher.dispatchAction("ButtonClick", "Decline", { productSku: selectedProduct.productSku });

        const response = await postDeclination({
            sessionId: getSelectedSessionId(),
            partner,
            productOfferId: selectedProduct.id,
            productSku: selectedProduct.productSku,
        });
        if (response) {
            useProductOfferStore.getState().updateProduct(sessionId, selectedProduct.id, {
                ...selectedProduct,
                saleProgress: SaleProgress.Declined,
            });
        } else {
            notification.error("There was an error processing the declination");
        }
        setIsLoadingDecline(false);
        closeAndClear();
    };

    const isBlocklistedCode =
        (blocklistedConfirmationCodes && blocklistedConfirmationCodes.includes(codeValue)) ?? false;

    //if this is true, disable the enroll button
    const confirmationCodeValidation = selectedProduct.confirmationCodePattern
        ? new RegExp(selectedProduct.confirmationCodePattern).test(codeValue)
        : true;

    return (
        <Modal
            centered
            classNames={{
                root: classes.modalRoot,
                content: classes.modalContent,
                title: classes.modalTitle,
                header: classes.modalHeader,
                overlay: classes.modalOverlay,
                inner: classes.modalInner,
            }}
            onClose={closeAndClear}
            opened={opened}
            withCloseButton={false}
            size="lg"
        >
            <CloseButton onClick={closeAndClear} className={classes.closeButton} />
            <Title order={2} className={classes.modalTitle}>
                Track Sale
            </Title>
            {[
                "ATT_AAL",
                "ATT_PA1_NOT_WITH_AAL",
                "ATT_PA4_NOT_WITH_AAL",
                "ATT_AAL_DATA",
                "ATT_AAL_VOICE",
                "ATT_AAL_WEARABLE",
            ].includes(selectedProduct.productSku) && partner === "att" ? (
                <Flex className={classes.topPadding} direction="column">
                    <Text className={classes.descriptionText}>Enter the confirmation code from OPUS</Text>
                    <Input
                        onChange={(event) => {
                            setCodeValue(event.currentTarget.value);
                        }}
                        component={IMaskInput}
                        defaultValue={codeValue}
                        placeholder="Confirmation code"
                        size="md"
                        mask="00-000000000000000"
                        classNames={{ input: classes.styledInput }}
                    />
                    {!["ATT_PA1_NOT_WITH_AAL", "ATT_PA4_NOT_WITH_AAL"].includes(selectedProduct.productSku) && (
                        <Select
                            classNames={{
                                input: classes.select,
                                label: classes.descriptionText,
                                dropdown: classes.optionsRoot,
                            }}
                            pt="xl"
                            onChange={(value) => {
                                if (value === "Yes, Protect Advantage 1") {
                                    setAdditionalSku("ATT_PA1_STATIC");
                                } else if (value === "Yes, Protect Advantage 4") {
                                    setAdditionalSku("ATT_PA4_STATIC");
                                }
                                if (value === "No") {
                                    setAdditionalSku("NO");
                                }
                            }}
                            size="md"
                            comboboxProps={{ zIndex: 1000 }}
                            label="Did you enroll them in anything else?"
                            placeholder="Choose an option"
                            data={["No", "Yes, Protect Advantage 1", "Yes, Protect Advantage 4"]}
                        />
                    )}
                </Flex>
            ) : (
                <>
                    {selectedProduct.confirmationCodePattern ? (
                        <Box className={classes.inputWrapper}>
                            <Text className={classes.orderNumberText}>
                                {productInfo?.confirmationCodeTitle ?? "Enter the order number"}
                            </Text>
                            <Input
                                onChange={(event) => {
                                    setCodeValue(event.currentTarget.value);
                                }}
                                component={IMaskInput}
                                defaultValue={codeValue}
                                placeholder={productInfo?.confirmationCodePlaceholder ?? "Order number"}
                                size="lg"
                                mask={/^[a-zA-Z0-9]*$/}
                                classNames={{ input: classes.styledInput }}
                            />
                        </Box>
                    ) : null}
                </>
            )}
            {productInfo?.confirmationCheckboxText && (
                <Checkbox
                    c="dark.1"
                    label={productInfo.confirmationCheckboxText}
                    checked={isConfirmationChecked}
                    onChange={(event) => setIsConfirmationChecked(event.currentTarget.checked)}
                />
            )}
            {productInfo?.subtext && (
                <Text c="dark.2" size="sm" mt="xl">
                    {productInfo.subtext}
                </Text>
            )}
            <Group className={classes.buttonGroup}>
                <Button
                    loading={isLoadingDecline}
                    disabled={isLoadingEnroll || isLoadingDecline}
                    className={classes.declineButton}
                    onClick={() => void onDecline()}
                    variant="subtle"
                    color="white"
                >
                    They declined
                </Button>
                <Button
                    loading={isLoadingEnroll}
                    disabled={
                        (!additionalSku &&
                            ["ATT_AAL", "ATT_AAL_DATA", "ATT_AAL_VOICE", "ATT_AAL_WEARABLE"].includes(
                                selectedProduct.productSku,
                            )) ||
                        isLoadingEnroll ||
                        isLoadingDecline ||
                        !confirmationCodeValidation ||
                        isBlocklistedCode ||
                        (!!productInfo?.confirmationCheckboxText && !isConfirmationChecked)
                    }
                    className={classes.enrollButton}
                    onClick={() => void onAccept()}
                    radius="xl"
                    variant="default"
                >
                    I enrolled the customer
                </Button>
            </Group>
        </Modal>
    );
}
